import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { BarChart3, Plus, Save, X, Edit3, Trash2, TrendingDown, TrendingUp, Minus, Printer, Target, Zap, Sparkles } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  KPI REGISTER — NABH quality indicators monthly tracking.
// ═══════════════════════════════════════════════════════════════════

const CATEGORIES = {
  safety:     { label: 'Safety',     color: '#dc2626' },
  quality:    { label: 'Quality',    color: '#2563eb' },
  efficiency: { label: 'Efficiency', color: '#0891b2' },
  patient:    { label: 'Patient',    color: '#7c3aed' },
  staff:      { label: 'Staff',      color: '#db2777' },
  financial:  { label: 'Financial',  color: '#16a34a' },
  other:      { label: 'Other',      color: '#6b7280' },
};

const fmtMonth = (m) => {
  const [y, mm] = m.split('-');
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

const evaluateStatus = (value, target, direction) => {
  if (value == null || target == null) return { color: '#6b7280', icon: <Minus size={12} /> };
  if (direction === 'lower-is-better') {
    if (value <= target) return { color: '#16a34a', icon: <TrendingDown size={12} /> };
    if (value <= target * 1.2) return { color: '#f59e0b', icon: <Minus size={12} /> };
    return { color: '#dc2626', icon: <TrendingUp size={12} /> };
  }
  if (value >= target) return { color: '#16a34a', icon: <TrendingUp size={12} /> };
  if (value >= target * 0.8) return { color: '#f59e0b', icon: <Minus size={12} /> };
  return { color: '#dc2626', icon: <TrendingDown size={12} /> };
};

const KPIRegisterPage = ({ API_BASE, currentUser }) => {
  const [dashboard, setDashboard] = useState({ months: [], grid: [] });
  const [metrics, setMetrics] = useState([]);
  const [autoCodes, setAutoCodes] = useState([]); // metric codes that support auto-compute
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [editCell, setEditCell] = useState(null);
  const [autoBusy, setAutoBusy] = useState(null); // cellKey currently auto-filling
  const [showMetricForm, setShowMetricForm] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, mets, auto] = await Promise.all([
        axios.get(`${API_BASE}/kpi/dashboard?months=${months}`),
        axios.get(`${API_BASE}/kpi/metrics`),
        axios.get(`${API_BASE}/kpi/auto-compute/supported`).catch(() => ({ data: [] })),
      ]);
      setDashboard(dash.data);
      setMetrics(mets.data);
      setAutoCodes(auto.data || []);
    } catch { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE, months]);

  // Auto-compute a single cell from raw clinic data, save it, and reload.
  const autoFillCell = async (metric, mo) => {
    const cellKey = `${metric._id}_${mo}`;
    setAutoBusy(cellKey);
    try {
      const r = await axios.get(`${API_BASE}/kpi/auto-compute`, { params: { metricCode: metric.code, month: mo } });
      const { value, numerator, denominator, formula } = r.data;
      await axios.post(`${API_BASE}/kpi/entries`, {
        metricId: metric._id, month: mo, value: Number(value),
        numerator: numerator ?? undefined, denominator: denominator ?? undefined,
        notes: `⚡ Auto-computed: ${formula}`,
        recordedBy: currentUser?.fullName || 'auto',
      });
      showMsg('success', `⚡ ${metric.code} auto-filled for ${fmtMonth(mo)}: ${value} ${metric.unit}`);
      load();
    } catch (e) { showMsg('error', 'Auto-fill failed: ' + (e.response?.data?.error || e.message)); }
    finally { setAutoBusy(null); }
  };

  // One-click: auto-fill ALL supported metrics for the current month.
  const autoFillCurrentMonth = async () => {
    const now = new Date();
    const mo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const supported = metrics.filter(m => autoCodes.includes(m.code));
    if (!supported.length) { showMsg('error', 'No auto-fill metrics found. Seed NABH benchmarks first.'); return; }
    let done = 0, fail = 0;
    for (const m of supported) {
      try {
        const r = await axios.get(`${API_BASE}/kpi/auto-compute`, { params: { metricCode: m.code, month: mo } });
        await axios.post(`${API_BASE}/kpi/entries`, {
          metricId: m._id, month: mo, value: Number(r.data.value),
          numerator: r.data.numerator ?? undefined, denominator: r.data.denominator ?? undefined,
          notes: `⚡ Auto-computed: ${r.data.formula}`,
          recordedBy: currentUser?.fullName || 'auto',
        });
        done++;
      } catch { fail++; }
    }
    showMsg('success', `⚡ Auto-filled ${done} metrics for ${fmtMonth(mo)}${fail ? ` (${fail} failed)` : ''}`);
    load();
  };

  const seedBenchmarks = async () => {
    if (!window.confirm('NABH ke standard 10 KPI benchmarks add karein? (already-existing metrics skip honge)')) return;
    try {
      const r = await axios.post(`${API_BASE}/kpi/seed-nabh`);
      showMsg('success', `${r.data.inserted} new metrics added, ${r.data.skipped} already exist`);
      load();
    } catch (e) { showMsg('error', 'Seed failed: ' + (e.response?.data?.error || e.message)); }
  };

  useEffect(() => { load(); }, [load]);

  const saveEntry = async (metricId, month, value, notes, num, den) => {
    try {
      await axios.post(`${API_BASE}/kpi/entries`, {
        metricId, month, value: Number(value),
        numerator: num !== '' && num != null ? Number(num) : undefined,
        denominator: den !== '' && den != null ? Number(den) : undefined,
        notes, recordedBy: currentUser?.fullName || 'admin',
      });
      showMsg('success', 'Saved'); load(); setEditCell(null);
    } catch (e) { showMsg('error', 'Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const deleteMetric = async (m) => {
    if (!window.confirm(`"${m.name}" disable karein?`)) return;
    try { await axios.delete(`${API_BASE}/kpi/metrics/${m._id}`); showMsg('success', 'Disabled'); load(); }
    catch { showMsg('error', 'Delete failed'); }
  };

  const printReport = () => {
    const w = window.open('', '_blank', 'width=1100,height=800');
    const rows = dashboard.grid.map(r => `
      <tr>
        <td><strong>${r.metric.code}</strong> — ${r.metric.name}</td>
        <td>${r.metric.target ?? '—'} ${r.metric.unit}</td>
        ${r.values.map(v => `<td style="text-align:center">${v ? v.value + ' ' + r.metric.unit : '—'}</td>`).join('')}
      </tr>`).join('');
    w.document.write(`<!doctype html><html><head><title>KPI Register — ${months} month report</title>
      <style>body{font-family:'Georgia',serif;max-width:1000px;margin:24px auto;padding:0 24px}
      h1{color:#0f172a;border-bottom:2px solid #0f172a;padding-bottom:8px}
      table{width:100%;border-collapse:collapse;font-size:11pt;margin-top:16px}
      th,td{border:1px solid #cbd5e1;padding:6px 10px}
      th{background:#f1f5f9;font-size:10pt;text-align:left}
      @page{size:A3 landscape;margin:15mm}</style></head><body>
      <h1>KPI Register — NABH Quality Indicators</h1>
      <div>Generated: ${new Date().toLocaleString('en-IN')} · Last ${months} months</div>
      <table><thead><tr><th>Metric</th><th>Target</th>${dashboard.months.map(m => `<th>${fmtMonth(m)}</th>`).join('')}</tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.onload=()=>window.print();</script></body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><BarChart3 size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> KPI Register</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {metrics.length} NABH quality indicators · click any cell to enter monthly value
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={months} onChange={e => setMonths(Number(e.target.value))} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.18)', color: 'white' }}>
              {[6, 12, 18, 24, 36].map(n => <option key={n} value={n} style={{ color: '#111' }}>{n} months</option>)}
            </select>
            <button onClick={printReport} style={headBtn}><Printer size={14} style={{ marginRight: 4 }} /> Print</button>
            <button onClick={autoFillCurrentMonth} title="Auto-fill all supported KPIs for this month from clinic data"
              style={{ ...headBtn, background: 'rgba(255,255,255,0.95)', color: theme.primaryDark, border: 'none', fontWeight: 700 }}>
              <Zap size={14} style={{ marginRight: 4 }} /> Auto-fill This Month
            </button>
            {isAdmin && (
              <>
                <button onClick={seedBenchmarks} title="Insert NABH-standard KPI benchmarks (OPD wait, sentinel, hand hygiene, etc.)"
                  style={headBtn}>
                  <Sparkles size={14} style={{ marginRight: 4 }} /> Seed NABH Benchmarks
                </button>
                <button onClick={() => { setEditingMetric(null); setShowMetricForm(true); }} style={headBtn}>
                  <Plus size={14} style={{ marginRight: 4 }} /> New Metric
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: theme.bg }}>
                <th style={{ ...th, minWidth: 260, position: 'sticky', left: 0, background: theme.bg, zIndex: 2 }}>Metric</th>
                <th style={{ ...th, minWidth: 70 }}>Target</th>
                {dashboard.months.map(m => (
                  <th key={m} style={{ ...th, minWidth: 86, textAlign: 'center' }}>{fmtMonth(m)}</th>
                ))}
                {isAdmin && <th style={{ ...th, minWidth: 80 }}></th>}
              </tr>
            </thead>
            <tbody>
              {dashboard.grid.map(row => {
                const cat = CATEGORIES[row.metric.category] || CATEGORIES.other;
                return (
                  <tr key={row.metric._id} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                    <td style={{ ...td, position: 'sticky', left: 0, background: 'white', zIndex: 1, borderRight: `2px solid ${cat.color}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: cat.color, background: cat.color + '18', padding: '2px 6px', borderRadius: 4 }}>{cat.label}</span>
                        <span style={{ fontSize: 10.5, color: theme.textMuted }}>{row.metric.code}</span>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: theme.navy, marginTop: 2 }}>{row.metric.name}</div>
                      {row.metric.formula && <div style={{ fontSize: 10, color: theme.textMuted, fontStyle: 'italic' }}>{row.metric.formula}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: theme.textMuted, fontSize: 11.5 }}>
                      {row.metric.target != null ? `${row.metric.target} ${row.metric.unit}` : '—'}
                      <div style={{ fontSize: 9, color: theme.textSubtle }}>{row.metric.targetDirection === 'lower-is-better' ? '↓ lower' : '↑ higher'}</div>
                    </td>
                    {row.values.map((entry, idx) => {
                      const mo = dashboard.months[idx];
                      const cellKey = `${row.metric._id}_${mo}`;
                      const isEditing = editCell === cellKey;
                      const isAutoBusy = autoBusy === cellKey;
                      const status = entry ? evaluateStatus(entry.value, row.metric.target, row.metric.targetDirection) : null;
                      const supportsAuto = autoCodes.includes(row.metric.code);
                      return (
                        <td key={mo} style={{ ...td, padding: 4, textAlign: 'center', background: status ? status.color + '0f' : 'transparent', cursor: 'pointer', position: 'relative' }}
                          onClick={() => !isEditing && setEditCell(cellKey)}>
                          {isEditing ? (
                            <CellEditor metric={row.metric} month={mo} entry={entry}
                              onSave={(v, n, num, den) => saveEntry(row.metric._id, mo, v, n, num, den)}
                              onCancel={() => setEditCell(null)} />
                          ) : entry ? (
                            <div style={{ fontWeight: 700, color: status.color, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                              {status.icon}{entry.value}<span style={{ fontSize: 10, fontWeight: 400, color: theme.textMuted }}>{row.metric.unit}</span>
                            </div>
                          ) : supportsAuto ? (
                            <button onClick={(e) => { e.stopPropagation(); autoFillCell(row.metric, mo); }}
                              disabled={isAutoBusy}
                              title={`⚡ Auto-fill ${row.metric.name} from raw data`}
                              style={{ background: 'transparent', border: `1px dashed ${theme.primary}66`, borderRadius: 4, color: theme.primaryDark,
                                padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: isAutoBusy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <Zap size={10} /> {isAutoBusy ? '…' : 'Auto'}
                            </button>
                          ) : (
                            <span style={{ color: theme.textSubtle, fontSize: 11 }}>—</span>
                          )}
                        </td>
                      );
                    })}
                    {isAdmin && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => { setEditingMetric(row.metric); setShowMetricForm(true); }} style={smallBtn}><Edit3 size={11} /></button>
                        <button onClick={() => deleteMetric(row.metric)} style={{ ...smallBtn, color: theme.danger, borderColor: theme.danger + '44', marginLeft: 3 }}><Trash2 size={11} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showMetricForm && (
        <MetricFormModal metric={editingMetric}
          onClose={() => { setShowMetricForm(false); setEditingMetric(null); }}
          onSave={async (data) => {
            try {
              if (editingMetric) await axios.put(`${API_BASE}/kpi/metrics/${editingMetric._id}`, data);
              else await axios.post(`${API_BASE}/kpi/metrics`, data);
              setShowMetricForm(false); setEditingMetric(null); load(); showMsg('success', 'Saved');
            } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
          }} />
      )}
    </div>
  );
};

// Inline cell editor
const CellEditor = ({ metric, month, entry, onSave, onCancel }) => {
  const [value, setValue] = useState(entry?.value ?? '');
  const [num, setNum] = useState(entry?.numerator ?? '');
  const [den, setDen] = useState(entry?.denominator ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 80 }} onClick={e => e.stopPropagation()}>
      <input autoFocus type="number" step="any" value={value} onChange={e => setValue(e.target.value)} placeholder="value"
        style={{ fontSize: 13, padding: '3px 6px', border: `1px solid ${theme.primary}`, borderRadius: 4, width: '100%', boxSizing: 'border-box' }}
        onKeyDown={e => { if (e.key === 'Enter') onSave(value, notes, num, den); if (e.key === 'Escape') onCancel(); }} />
      <div style={{ display: 'flex', gap: 2 }}>
        <button onClick={() => onSave(value, notes, num, den)} style={{ ...smallBtn, padding: '2px 6px', fontSize: 10, background: theme.primary + '22', color: theme.primaryDark, flex: 1 }}><Save size={10} /></button>
        <button onClick={onCancel} style={{ ...smallBtn, padding: '2px 6px', fontSize: 10, flex: 1 }}><X size={10} /></button>
      </div>
    </div>
  );
};

const MetricFormModal = ({ metric, onClose, onSave }) => {
  const [f, setF] = useState(metric || {
    code: '', name: '', category: 'quality', description: '', formula: '',
    unit: '%', target: '', targetDirection: 'lower-is-better', frequency: 'monthly', order: 100,
  });
  return (
    <div style={modalBg} onClick={onClose}>
      <form onSubmit={e => { e.preventDefault(); onSave({ ...f, target: f.target === '' ? null : Number(f.target) }); }} style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', color: theme.navy }}>{metric ? 'Edit KPI Metric' : 'New KPI Metric'}</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <div><label style={lbl}>Code *</label><input required value={f.code} onChange={e => setF({ ...f, code: e.target.value.toUpperCase() })} style={inputStyle} /></div>
            <div><label style={lbl}>Name *</label><input required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} style={inputStyle} /></div>
          </div>
          <div><label style={lbl}>Category</label>
            <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} style={inputStyle}>
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Formula / Calculation</label>
            <input value={f.formula} onChange={e => setF({ ...f, formula: e.target.value })} style={inputStyle} placeholder="e.g. (X / Y) × 100" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Unit</label><input value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} style={inputStyle} placeholder="%, count, hrs, ₹" /></div>
            <div><label style={lbl}>Target</label><input type="number" step="any" value={f.target ?? ''} onChange={e => setF({ ...f, target: e.target.value })} style={inputStyle} /></div>
            <div><label style={lbl}>Direction</label>
              <select value={f.targetDirection} onChange={e => setF({ ...f, targetDirection: e.target.value })} style={inputStyle}>
                <option value="lower-is-better">Lower is better</option>
                <option value="higher-is-better">Higher is better</option>
              </select>
            </div>
          </div>
          <div><label style={lbl}>Description</label><textarea rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} style={{ ...inputStyle, fontFamily: 'inherit' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={smallBtn}>Cancel</button>
          <button type="submit" style={{ ...smallBtn, background: theme.primary, color: 'white', borderColor: theme.primary }}>Save</button>
        </div>
      </form>
    </div>
  );
};

const headBtn = { ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' };
const th = { padding: '9px 10px', fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left' };
const td = { padding: '8px 10px', color: theme.navy, verticalAlign: 'middle' };
const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 5, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: theme.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 };
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalStyle = { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '92vh', overflowY: 'auto' };

export default KPIRegisterPage;
